import { createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import TitleRow from '@/components/ui/TitleRow'
import { OverviewBoardSkeleton } from '@/screens/brokers/components/overview/OverviewBoardStates'
import FloorStatsStrip from '@/screens/brokers/components/overview/FloorStatsStrip'
import StocksTradedMostCard from '@/screens/brokers/components/overview/StocksTradedMostCard'
import TopBrokersCard from '@/screens/brokers/components/overview/TopBrokersCard'
import { TRADED_PAIR } from '@/screens/brokers/components/overview/overview-layout'
import TradedSectorsCard from '@/screens/brokers/components/overview/TradedSectorsCard'
import { loadBrokersOverview } from '@/screens/brokers/OverviewScreen.loader'
import { overviewSearchSchema } from '@/screens/brokers/search'

/** Known without data, so the pending and failed pages draw the real one. */
const HEADER = <TitleRow title="Broker Chirfaar" />

/**
 * The route waits only on the overview query (`FloorStatsStrip` and
 * `TopBrokersCard`); the two traded-most cards are secondary reads that draw
 * their own skeletons. A first load still shows ONE page-wide skeleton shaped
 * like the whole loaded board, every card included: their boxes are part of
 * the page's real layout, and drawing them only after the fetch settles would
 * make the page grow once it does.
 */
function OverviewPending() {
  return (
    <ScreenLayout
      pending
      pendingLabel="Loading the broker board…"
      header={HEADER}
      pageTitle="Broker Chirfaar"
    >
      <OverviewBoardSkeleton />
    </ScreenLayout>
  )
}

function OverviewError(props: ErrorComponentProps) {
  return (
    <RouteErrorPage
      {...props}
      header={HEADER}
      title="The broker board did not load"
      description="The data did not come back. Nothing on your device is wrong."
    />
  )
}

export const Route = createFileRoute('/_app/brokers')({
  validateSearch: overviewSearchSchema,
  loader: loadBrokersOverview,
  pendingComponent: OverviewPending,
  errorComponent: OverviewError,
  component: OverviewScreen,
})

/**
 * Brokers · Overview, to the revamp mock (`#/brokers`, 2026-10-28).
 *
 * "Top brokers today" and the floor stats read `/api/v5/brokers/overview/`.
 * Sectors traded most reads `/api/v5/brokers/sector-flow/` (plus
 * `/busy-names/?sector=` for each row's names). Stocks traded most's Bought
 * most / Sold most table is built but not served yet
 * (`STOCKS_TRADED_SIDES_AVAILABLE`), and sits beside Sectors as the mock's
 * pair. "Today's highlights" lives in the Broker rail, which `ScreenLayout`
 * picks by route. No source footnote: the mock draws none.
 *
 * No `period` switcher is drawn, but the route still validates one
 * (`overviewSearchSchema`) and this screen reads and forwards it — a deep
 * link or a future family-level control can set it, and `FloorStatsStrip`
 * captions the `isPartialWindow` case regardless of whether anything on
 * screen lets a member change it today.
 */
function OverviewScreen() {
  const { period } = Route.useSearch()

  return (
    <ScreenLayout header={HEADER} pageTitle="Broker Chirfaar">
      <FloorStatsStrip period={period} />

      <TopBrokersCard period={period} />
      <div className={TRADED_PAIR}>
        <TradedSectorsCard period={period} />
        <StocksTradedMostCard period={period} />
      </div>
    </ScreenLayout>
  )
}
